import { useState, useCallback, useEffect, useMemo } from 'react';
import { FileText, X } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import './Bills.css';

const PAGE_SIZE = 10;

const CONSUME_TYPES = ['生成视频', '声音克隆', 'API 调用', '生成图片', '工作流任务'];

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const normalizeEvent = (item, idx) => ({
  id:         item.id         || `ev-${idx}`,
  chargeType: item.chargeType || item.charge_type || '未知类型',
  amount:     Number(item.amount) || 0,
  createdAt:  item.createdAt  || item.created_at  || '',
});

const normalizeBenefit = (item, idx) => ({
  id:         `ben-${idx}`,
  chargeType: item.name       || '套餐',
  total:      Number(item.total)  || 0,
  used:       Number(item.used)   || 0,
  createdAt:  item.startedAt  || item.started_at  || '',
  endAt:      item.endAt      || item.end_at      || '',
});

const Bills = () => {
  const [view, setView]                 = useState('consume');
  const [items, setItems]               = useState([]);
  const [totalItems, setTotalItems]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [apiError, setApiError]         = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [currentPage, setCurrentPage]   = useState(1);

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '账单明细', path: null },
  ];

  const isConsumeTab = view === 'consume';

  // ── Fetch ──
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setApiError('');
      try {
        let fetchedItems = [];
        let total = 0;

        if (isConsumeTab) {
          const res = await fetch(
            `/api/billing/benefit_events?page=${currentPage}&pageSize=${PAGE_SIZE}`,
            { credentials: 'include' }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.code !== undefined && data.code !== 0) throw new Error(data.msg || `Error ${data.code}`);
          const d = data?.data ?? data ?? {};
          fetchedItems = (d.items ?? []).map(normalizeEvent);
          total = Number(d.total ?? fetchedItems.length);
        } else {
          // gain tab: subscription packages (not paginated)
          const res = await fetch('/api/billing/benefits', { credentials: 'include' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.code !== undefined && data.code !== 0) throw new Error(data.msg || `Error ${data.code}`);
          const d = data?.data ?? data ?? {};
          fetchedItems = (d.items ?? []).map(normalizeBenefit);
          total = fetchedItems.length;
        }

        if (!cancelled) {
          setItems(fetchedItems);
          setTotalItems(total);
        }
      } catch (err) {
        if (!cancelled) {
          setApiError(err.message || '加载失败');
          setItems([]);
          setTotalItems(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [view, currentPage, isConsumeTab]);

  // ── Client-side filter ──
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (isConsumeTab && selectedType !== 'all' && item.chargeType !== selectedType) return false;
      if (dateFrom && item.createdAt.slice(0, 10) < dateFrom) return false;
      if (dateTo   && item.createdAt.slice(0, 10) > dateTo)   return false;
      return true;
    });
  }, [items, selectedType, dateFrom, dateTo, isConsumeTab]);

  const hasFilters = (isConsumeTab && selectedType !== 'all') || !!dateFrom || !!dateTo;

  // Consume: server-side pagination; Gain: client-side pagination
  // When filters are active on consume tab, use filtered count for pagination
  const totalPages = isConsumeTab
    ? (Math.ceil((hasFilters ? filteredItems.length : totalItems) / PAGE_SIZE) || 1)
    : (Math.ceil(filteredItems.length / PAGE_SIZE) || 1);

  const displayItems = isConsumeTab
    ? filteredItems
    : filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Count: server total when no filter; filtered count when filter active
  const displayTotal = hasFilters ? filteredItems.length : totalItems;

  // ── Reset when switching tabs ──
  useEffect(() => {
    setCurrentPage(1);
    setSelectedType('all');
    setDateFrom('');
    setDateTo('');
  }, [view]);

  // ── Clamp page ──
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleViewSwitch = useCallback((nextView) => {
    if (nextView === view) return;
    setView(nextView);
  }, [view]);

  const handleTypeSelect  = (type) => { setSelectedType(type); setCurrentPage(1); };
  const handleDateFrom    = (e)    => { setDateFrom(e.target.value); setCurrentPage(1); };
  const handleDateTo      = (e)    => { setDateTo(e.target.value); setCurrentPage(1); };
  const handleClearFilters = () => { setSelectedType('all'); setDateFrom(''); setDateTo(''); setCurrentPage(1); };
  const handlePageChange  = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="bl-page">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header */}
      <div className="bl-page-header">
        <h1 className="bl-page-title">账单明细</h1>
      </div>

      {/* Content Card */}
      <div className="bl-card">

        {/* Card Header: consume/gain tabs + invoice link */}
        <div className="bl-card-header">
          <div className="bl-tabs">
            <button
              className={`bl-tab${view === 'consume' ? ' active' : ''}`}
              onClick={() => handleViewSwitch('consume')}
            >
              消耗积分
            </button>
            <button
              className={`bl-tab${view === 'gain' ? ' active' : ''}`}
              onClick={() => handleViewSwitch('gain')}
            >
              获取积分
            </button>
          </div>
          <a
            href="https://uvetjcpbqd.feishu.cn/share/base/form/shrcncONhVq5IDA6ck0vIwnZfAc"
            target="_blank"
            rel="noopener noreferrer"
            className="bl-invoice-link"
          >
            申请开发票
          </a>
        </div>

        {/* Filter Bar */}
        <div className="bl-filter-bar">
          {/* Type filter (consume tab only) */}
          {isConsumeTab && (
            <div className="bl-filter-row">
              <span className="bl-filter-label">类型</span>
              <div className="bl-type-pills">
                <button
                  className={`bl-type-pill${selectedType === 'all' ? ' active' : ''}`}
                  onClick={() => handleTypeSelect('all')}
                >
                  全部
                </button>
                {CONSUME_TYPES.map((type) => (
                  <button
                    key={type}
                    className={`bl-type-pill${selectedType === type ? ' active' : ''}`}
                    onClick={() => handleTypeSelect(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date range filter */}
          <div className="bl-filter-row">
            <span className="bl-filter-label">时间段</span>
            <div className="bl-date-range">
              <input
                type="date"
                className="bl-date-input"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={handleDateFrom}
              />
              <span className="bl-date-sep">至</span>
              <input
                type="date"
                className="bl-date-input"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={handleDateTo}
              />
            </div>
            {hasFilters && (
              <button className="bl-clear-btn" onClick={handleClearFilters}>
                <X size={12} />
                清除筛选
              </button>
            )}
            <span className="bl-filter-count">
              共 {displayTotal} 条{hasFilters && isConsumeTab ? '（当前页）' : ''}
            </span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <LoadingSpinner />
          </div>
        ) : apiError ? (
          <div className="bl-empty">
            <p className="bl-empty-text" style={{ color: '#dc2626' }}>加载失败：{apiError}</p>
            <button
              style={{ marginTop: 8, fontSize: 13, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => setCurrentPage((p) => p)}
            >重试</button>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bl-table-wrap">
              <table className="bl-table">
                <thead>
                  <tr>
                    <th>{isConsumeTab ? '变更项目' : '套餐名称'}</th>
                    <th>{isConsumeTab ? '时间' : '开始时间'}</th>
                    {!isConsumeTab && <th>结束时间</th>}
                    <th className="bl-th-amount">{isConsumeTab ? '数量' : '剩余 / 总量'}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.length === 0 ? (
                    <tr>
                      <td colSpan={isConsumeTab ? 3 : 4} className="bl-empty-cell">
                        <div className="bl-empty">
                          <FileText size={40} className="bl-empty-icon" />
                          <p className="bl-empty-text">
                            {hasFilters ? '没有符合条件的记录' : isConsumeTab ? '暂无消耗记录' : '暂无套餐记录'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : isConsumeTab ? (
                    displayItems.map((item) => (
                      <tr key={item.id} className="bl-row">
                        <td className="bl-td-type">{item.chargeType}</td>
                        <td className="bl-td-time">{formatDate(item.createdAt)}</td>
                        <td className={`bl-td-amount${item.amount < 0 ? ' consume' : ' gain'}`}>
                          {item.amount < 0 ? item.amount : `+${item.amount}`}
                        </td>
                      </tr>
                    ))
                  ) : (
                    displayItems.map((item) => (
                      <tr key={item.id} className="bl-row">
                        <td className="bl-td-type">{item.chargeType}</td>
                        <td className="bl-td-time">{formatDate(item.createdAt)}</td>
                        <td className="bl-td-time">{formatDate(item.endAt)}</td>
                        <td className="bl-td-amount gain">
                          {item.total - item.used} / {item.total}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bl-pagination">
                <button
                  className="bl-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >←</button>

                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let n;
                  if (totalPages <= 7)                    n = i + 1;
                  else if (currentPage <= 4)               n = i + 1;
                  else if (currentPage >= totalPages - 3)  n = totalPages - 6 + i;
                  else                                     n = currentPage - 3 + i;
                  return (
                    <button
                      key={n}
                      className={`bl-page-num${currentPage === n ? ' active' : ''}`}
                      onClick={() => handlePageChange(n)}
                    >{n}</button>
                  );
                })}

                <button
                  className="bl-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >→</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Bills;
